import React from 'react';
import { useIntl } from "react-intl";
import { useAtomValue } from "jotai";
import classNames from "classnames";
import synonymRepository from "@/repository/synonymRepository";
import { usePaginatedSynonyms } from "@/query/synonym";
import { singleTestPanelOpenAtom } from "@/atom/singleTestPanel";
import { useAgentId, useModal, usePageParam } from "@/hook";
import Button from "@/component/Button";
import { SearchBar } from "@/component/input";
import { UploadModal } from "@/component/modal";
import PaginationBar from "@/component/PaginationBar";
import { SingleTestPanel, SingleTestButton } from "@/component/singleTest";
import { DownloadIcon, UploadIcon } from "@/assets/svgs/icons";

import SynonymTable from "./component/SynonymTable";
import SynonymEditForm from "./component/SynonymEditForm";


const SYNONYM_PAGE_SIZE = 15;
const SYNONYM_UPLOAD_MODAL_ID = "synonym-upload-modal";

export interface SynonymPageParam {
    page: number;
    size: number;
    searchKeyword?: string;
    sortOrder: "asc" | "desc";
}

const SynonymView: React.FC = () => {
    const intl = useIntl();
    const agentId = useAgentId();
    const modal = useModal();

    const { param, set: setParam } = usePageParam<SynonymPageParam>({
        page: 0,
        size: SYNONYM_PAGE_SIZE,
        sortOrder: "asc",
    });

    const singleTestPanelOpen = useAtomValue(singleTestPanelOpenAtom);
    const { data: page } = usePaginatedSynonyms(
        agentId,
        param?.page,
        param?.size,
        param?.sortOrder,
        param?.searchKeyword
    );

    const onClickUpload = async (file: File, isCleanImport: boolean) => {
        await synonymRepository.import(agentId, file, isCleanImport);
    };

    return (<>
        <UploadModal id={SYNONYM_UPLOAD_MODAL_ID}
                     label={intl.formatMessage({ id: "i06007" })}
                     cleanImportText={intl.formatMessage({ id: "i06018" })}
                     sampleFileName="nlu_synonym_sample"
                     sampleFileColumns={[
                         { attribute: "keyword", description: intl.formatMessage({ id: "i06008" }) },
                         { attribute: "synonym", description: intl.formatMessage({ id: "i06009" }) }
                     ]}
                     upload={onClickUpload}
        />

        <div className={classNames(
                            "agent-content-layout",
                            singleTestPanelOpen && "test-panel-open"
                        )}
        >
            <div className={classNames(
                    "full-page-container flex flex-col gap-3",
                    singleTestPanelOpen && "test-panel-open"
                )}
            >
                <div className="search-bar-container">
                    <SearchBar lg width={680}
                               placeholder={intl.formatMessage({ id: "i06016" })}
                               onSearch={query => setParam({ searchKeyword: query })}
                    />
                    <div className="search-bar-container-btn-group">
                        <Button variant="text"
                                onClick={() => modal.open(SYNONYM_UPLOAD_MODAL_ID)}
                        >
                            <UploadIcon className="size-3.5" />
                            {intl.formatMessage({ id: "b0004" })}
                        </Button>
                        <Button variant="text"
                                onClick={() => synonymRepository.export(agentId)}
                        >
                            <DownloadIcon className="size-3.5" />
                            {intl.formatMessage({ id: "b0005" })}
                        </Button>
                        <SingleTestButton />
                    </div>
                </div>

                <div className="form-table-container">
                    <SynonymEditForm />
                    <div className="table-pagination-container">
                        <SynonymTable page={page}
                                      pageParam={param}
                                      onClickSortHead={order => setParam({ sortOrder: order })}
                        />
                        <PaginationBar pageData={page}
                                       onClickPage={page => setParam({ page: page })}
                        />
                    </div>

                    <SingleTestPanel className="ml-auto" />
                </div>
            </div>
        </div>
    </>);
};

export default SynonymView;